import {
  createDoczHandle,
  createStructuredDoczHandle,
  decodeBlockList,
  type DoczBlock,
  type DoczBlockKind,
  type DoczDocument,
  type DoczHandle,
  type DoczModule,
  loadDocz,
  type StructuredBlock,
} from "@norkron/web-docz";
import { base64ToBlockDtoBytes } from "./blockDtoWire";

/**
 * `lib/doczHandle` — the W2 edit-mode seam (design §1.9 · spec F1). Turns the
 * SERVER-EMITTED docz transport (`GET /api/documents/:id/docz`) into a live
 * `DoczHandle` the WYSIWYG `<ElaborateEditor>` mounts over. Zig owns the docz IR
 * (Rule Zero): there is no client `fromHtml` — the backend re-ingests
 * `rendered_html` into the canonical IR and ships the block transport, and this
 * module only wraps it (WASM `createDoczHandle`) so the editor's `toHtml()` can
 * feed the whole-resource save-back. The transport is fetched by the mock-aware
 * `articleApi.getDoczDocument` adapter method (never a raw fetch here), so the
 * workbench stays clickable with no backend.
 */

/** One block of the `GET /api/documents/:id/docz` transport (field names ARE the
 *  backend wire keys — `models.DoczTransport`). `kind` is the docz block kind as a
 *  raw string; unknown kinds normalise to `paragraph`. */
export interface DoczTransportBlock {
  readonly id: string;
  readonly text: string;
  readonly kind: string;
  readonly level?: number;
}

/** The `GET /api/documents/:id/docz` wire envelope (`models.DoczTransport`) — the
 *  ordered blocks + the head revision the block text is anchored against. This is
 *  a transport envelope, NOT the docz IR (the IR stays server-side). */
export interface DoczTransport {
  readonly doc_id: string | number;
  readonly base_version_id: string;
  readonly blocks: readonly DoczTransportBlock[];
  /**
   * THE FULL-FIDELITY CHANNEL (`models.DoczTransport.body_dto_b64`): the whole
   * body as the docz block-DTO list wire, base64. It carries runs, the
   * ordinal-keyed MARK set and per-run style — everything `blocks[].text`
   * structurally cannot.
   *
   * Deliberately the SAME wire `GET /api/documents/:id/branded-docz` emits and
   * `@norkron/web-docz`'s `block-dto.ts` decodes (Wave B: "block-dto.ts is THE
   * transport"). Empty when the body is empty or the server's codec declined the
   * list — read as "fall back to `blocks`".
   */
  readonly body_dto_b64?: string;
}

/** The media type this client can decode. The version is a media-type
 *  PARAMETER, so the future full-document representation is `v=2` on the SAME
 *  route — a negotiated bump, not a second endpoint. Bytes announced as any
 *  other version must be REFUSED rather than fed to this decoder: misdecoding a
 *  document silently is how a save destroys one. */
export const DOCUMENT_MEDIA_TYPE = "application/vnd.norkron.document;v=1";

/** The vendor type without parameters, for matching a `Content-Type` that may
 *  carry differently-spaced parameters. */
export const DOCUMENT_MEDIA_BASE = "application/vnd.norkron.document";

/** Where the server said the bytes came from (`X-Document-Source`). Anything other
 *  than `structured` means the reader is NOT looking at the stored typed IR. */
export type DoczBinarySource =
  | "structured"
  | "legacy-html"
  | "legacy-html-after-restore-failure"
  | "empty";

/**
 * The BINARY representation of `GET /api/documents/:id`, obtained by sending
 * `Accept: application/vnd.norkron.document;v=1`: the typed block list as RAW
 * BYTES plus the provenance headers.
 *
 * This replaces a JSON envelope that base64'd the very same bytes and shipped a
 * redundant `blocks[]` plain-text projection beside them — a projection that
 * structurally cannot carry a table, so the server re-serialized TYPED tables
 * back into `<table>` markup to fill it. Measured on document 85: 41,772 JSON
 * bytes to carry 16,537 bytes of body.
 */
export interface DoczBinaryTransport {
  readonly docId: string;
  readonly bytes: Uint8Array;
  readonly source: DoczBinarySource;
  /** 64 lowercase hex of the stored blob, or "" when there is no blob. */
  readonly hash: string;
  /** The served `Content-Type` — the representation's identity. */
  readonly contentType: string;
  readonly baseVersionId: string;
  /**
   * The `el.doc_versions.id` these bytes are the body of, when the caller asked
   * for an ARCHIVED VERSION rather than the head. Absent for the head.
   *
   * The server states it as `X-Document-Version`, and the fetcher REFUSES a
   * response whose header names a different revision than the one requested
   * (`api/articleUnits.fetchDocumentBinary`). Painting a revision nobody asked for
   * is the one failure a canvas cannot show you — there is no DOM to inspect — so
   * it is checked rather than assumed.
   */
  readonly versionId?: number;
}

const BLOCK_KINDS: ReadonlySet<string> = new Set<DoczBlockKind>([
  "paragraph",
  "heading",
  "list_item",
  "blockquote",
  "code",
  // Opaque preserved block (table / image / list / quote / code block) — `text`
  // is verbatim markup the editor mounts read-only, `hydrateLoadedTables`
  // renders, and `toHtml()` re-emits unchanged on save. This is the ONLY kind
  // through which a structured block survives edit→save, which is why the
  // server lowers every non-run-bearing `docz.Block` to it
  // (`src/ingest/html_ingest.zig` — `TransportKind.html`).
  "html",
]);

/**
 * Kinds the SERVER emits that are not themselves `DoczBlockKind`s, mapped
 * DELIBERATELY rather than swept into the unknown-kind fallback.
 *
 * `text` is docz's bare run-container block (`Block.text`): semantically a
 * paragraph, with no separate client kind. Aliasing it is lossless — its `text`
 * field already carries the concatenated run text.
 */
const KIND_ALIASES: Readonly<Record<string, DoczBlockKind>> = {
  text: "paragraph",
};

/**
 * Coerce a wire block-kind string to a `DoczBlockKind`.
 *
 * The server contract (`html_ingest.TransportKind`) is a CLOSED set of four
 * kinds — `heading` / `paragraph` / `text` / `html` — every one of which is
 * either in `BLOCK_KINDS` or in `KIND_ALIASES`. So the final `paragraph`
 * fallback should now be unreachable, and reaching it means the two ends have
 * drifted: a block would mount with the WRONG renderer and, for a structured
 * block, as an EMPTY paragraph whose next save destroys the content. That is
 * exactly the P0 that shipped when the server lowered a typed table to the
 * unknown kind `"table_v2"`, so it is reported instead of swallowed.
 */
export function normalizeKind(kind: string): DoczBlockKind {
  if (BLOCK_KINDS.has(kind)) return kind as DoczBlockKind;
  const alias = KIND_ALIASES[kind];
  if (alias) return alias;
  console.error(
    `[doczHandle] unknown block kind "${kind}" from the server transport — ` +
      `mounting as a paragraph. The server/client kind contracts have drifted; ` +
      `see html_ingest.TransportKind.`,
  );
  return "paragraph";
}

/**
 * Map the transport envelope to the web-docz `DoczDocument` the handle wraps.
 *
 * ── `base_version_id: ""` MEANS "THERE ISN'T ONE" (a shipped P0) ─────────────
 * The server emits this field as `COALESCE(d.content_sha256, '')`
 * (`db/versions_repo.zig:101`), so a document that has never been saved — the
 * NORMAL state of a project created with an empty brief — arrives with `""`.
 * `DoczDocumentSchema` types `baseVersionId` as `z.string().min(1).optional()`:
 * a real revision id, or absent. `""` is neither, so passing it straight through
 * made `createDoczHandle` throw at the boundary — and `ArticleWorkbench`'s
 * `toggleEdit` builds the handle BEFORE flipping the pane into edit, so the
 * whole "write it directly in the editor" deep-link presented as a control that
 * consumed its URL param and did nothing.
 *
 * The model was right and the mapping was wrong: the absence of a base version
 * is expressed by OMITTING the field, never by an empty string.
 */
function toDoczDocument(transport: DoczTransport): DoczDocument {
  const blocks: DoczBlock[] = transport.blocks.map((b) => {
    const kind = normalizeKind(b.kind);
    const block: DoczBlock = { id: b.id, text: b.text, kind };
    return kind === "heading" && b.level != null
      ? { ...block, level: b.level }
      : block;
  });
  const baseVersionId = transport.base_version_id;
  return {
    id: String(transport.doc_id),
    ...(baseVersionId ? { baseVersionId } : {}),
    blocks,
  };
}

// ── memoized WASM module (one instance across every pane / handle) ───────────
// loadDocz() is already a cached singleton in web-docz, but memoizing the
// promise here guarantees a single in-flight load even under concurrent
// enter-edit gestures on brief + article.
let modulePromise: Promise<DoczModule> | null = null;

/** The shared, memoized `docz.wasm` module. */
export function sharedDoczModule(): Promise<DoczModule> {
  modulePromise ??= loadDocz();
  return modulePromise;
}

/**
 * Decode the full-fidelity channel into structured blocks, or `null` when it is
 * absent / empty / undecodable.
 *
 * RULE ZERO: `decodeBlockList` is `@norkron/web-docz`'s own block-DTO reader —
 * the SAME one `<BrandedCanvasPreview>` feeds and the SAME wire the layout wasm
 * consumes. There is no second decoder here and there must never be one.
 *
 * Block ids are carried over from the plain-text channel POSITIONALLY, because
 * both channels are the same `doc.body` in the same order
 * (`src/handlers/doc_content.zig` builds them in one loop). This is
 * LOAD-BEARING: every stored comment anchors to `b{i}`, so losing the ids would
 * orphan every comment in the document at once.
 */
function structuredFromDto(transport: DoczTransport): StructuredBlock[] | null {
  const b64 = transport.body_dto_b64;
  if (!b64) return null;
  try {
    const blocks = decodeBlockList(base64ToBlockDtoBytes(b64));
    if (blocks.length === 0) return null;
    if (blocks.length !== transport.blocks.length) {
      // The two channels are the SAME `doc.body` in the same order: `getDocz`
      // builds `blocks[]` in one loop over it and `encodeBrandedBody` appends
      // exactly one DTO block per input block (it degrades, it never drops). A
      // length mismatch therefore means the wire is broken — and mounting the
      // short side would hand the editor a document that is MISSING BLOCKS,
      // which the next save writes to the real document permanently. An
      // unformatted mount is recoverable; a truncated one is not.
      console.error(
        `[doczHandle] body_dto_b64 decoded ${blocks.length} blocks but the transport ` +
          `carries ${transport.blocks.length} — REFUSING the full-fidelity channel and ` +
          `mounting the PLAIN-TEXT one, because mounting a truncated document would let ` +
          `the next save delete the missing blocks. The server's two channels have drifted.`,
      );
      return null;
    }
    return blocks.map((block, i) => {
      const id = transport.blocks[i]?.id;
      return (id != null ? { ...block, id } : block) as StructuredBlock;
    });
  } catch (err) {
    // A codec refusal must DEGRADE to the plain-text channel, never blank the
    // mount — the same non-blanking discipline the server applies when it
    // encodes. Loud, because the reviewer is about to see an unformatted
    // document and a save from it would flatten the real one.
    console.error(
      "[doczHandle] body_dto_b64 failed to decode — mounting the PLAIN-TEXT channel, " +
        "so this document will render and SAVE without inline formatting.",
      err,
    );
    return null;
  }
}

/**
 * Build a live `DoczHandle` from a server-emitted docz transport.
 *
 * PREFERS the full-fidelity block-DTO channel (`body_dto_b64`). Until it did,
 * the handle was built from `blocks[].text` — a plain-text projection that has
 * never carried marks — so **the canvas mounted an already-stripped document**
 * and a save, even one with no edit at all, faithfully persisted the stripped
 * version over the real one. MEASURED on the live corpus (2026-07-28): doc 11
 * stored 82 `<strong>`, the DTO channel carried all 82, `toBlocks()` produced
 * ZERO marks and the save payload came back with 21 — the survivors being only
 * those inside opaque `html` blocks, which ride through as verbatim markup.
 *
 * The returned handle's `toBlocks()` is the transport/save primitive; `toHtml()`
 * is what the whole-resource save-back currently posts
 * (`articleApi.saveResource(..., { rendered_html })`).
 */
export async function buildDoczHandle(
  transport: DoczTransport,
): Promise<DoczHandle> {
  const module = await sharedDoczModule();
  const structured = structuredFromDto(transport);
  if (structured) {
    return createStructuredDoczHandle(module, {
      id: String(transport.doc_id),
      baseVersionId: transport.base_version_id,
      blocks: structured,
    });
  }
  return createDoczHandle(module, toDoczDocument(transport));
}

/** Raised when the served `Content-Type` is not a representation this client
 *  can decode. */
/**
 * Does a served `Content-Type` name a representation this client decodes?
 *
 * Matches the vendor type and requires the version parameter to be one we
 * implement. An ABSENT version is accepted (the server's default is v=1); any
 * OTHER version is refused, because `v=2` will carry entirely different bytes.
 */
export function isSupportedRepresentation(contentType: string): boolean {
  const parts = contentType.split(";").map((s) => s.trim());
  const base = (parts.shift() ?? "").toLowerCase();
  if (base !== DOCUMENT_MEDIA_BASE) return false;
  const v = parts
    .map((p) => /^v\s*=\s*"?([^"]*)"?$/i.exec(p))
    .find((m) => m !== null);
  return v == null || v[1] === "1";
}

export class UnsupportedRepresentationError extends Error {
  readonly contentType: string;
  constructor(contentType: string) {
    super(
      `[doczHandle] server sent Content-Type "${contentType}" but this client ` +
        `decodes "${DOCUMENT_MEDIA_TYPE}". REFUSING to mount: guessing at the ` +
        `bytes would misdecode the document, and the next save would persist ` +
        `the wreckage.`,
    );
    this.name = "UnsupportedRepresentationError";
    this.contentType = contentType;
  }
}

/**
 * Report a non-`structured` mount. NOT silent, by construction.
 *
 * `legacy-html*` means the server could not use the stored typed IR and
 * re-ingested `rendered_html` instead, so the reader is looking at a lossy
 * reconstruction of the document rather than the document. Measured on the live
 * corpus (2026-07-29): 117 of 147 rows have no `body_docz` blob at all, so this
 * is the COMMON path today, not an edge case — which is exactly why it has to be
 * visible instead of quietly identical to a structured mount.
 */
export function describeDoczSource(source: DoczBinarySource): string | null {
  switch (source) {
    case "structured":
      return null;
    case "legacy-html":
      return "Reconstructed from stored HTML — this document has no structured body yet.";
    case "legacy-html-after-restore-failure":
      return "Structured body could not be read (codec version mismatch) — reconstructed from stored HTML.";
    case "empty":
      return "This document is empty.";
  }
}

/**
 * Build a live `DoczHandle` from the BINARY transport.
 *
 * Block ids are regenerated POSITIONALLY as `b{i}` — byte-identical to what the
 * server's JSON channel emitted (`std.fmt.allocPrint("b{d}", .{i})` in
 * `doc_content.zig`). This is LOAD-BEARING: every stored comment anchors to
 * `b{i}`, so the ids must not drift when the transport changes.
 */
export async function buildDoczHandleFromBinary(
  bin: DoczBinaryTransport,
): Promise<DoczHandle> {
  if (!isSupportedRepresentation(bin.contentType)) {
    throw new UnsupportedRepresentationError(bin.contentType);
  }

  const notice = describeDoczSource(bin.source);
  if (notice) {
    console.warn(
      `[doczHandle] document ${bin.docId} mounted with X-Document-Source="${bin.source}": ${notice}`,
    );
  }

  const module = await sharedDoczModule();
  const decoded = decodeBlockList(bin.bytes);
  const blocks = decoded.map(
    (block, i) => ({ ...block, id: `b${i}` }) as StructuredBlock,
  );
  return createStructuredDoczHandle(module, {
    id: bin.docId,
    baseVersionId: bin.baseVersionId,
    blocks,
  });
}
